<script setup lang="ts">
import { bankDetailsRules } from "@/validation";
import { PageEnum } from "@/enums";
import { useBankingStore, useDataLayerStore, useGlobalStore } from "@/stores";
import useVuelidate, { type ErrorObject } from "@vuelidate/core";
import { AdobeEventTypeEnum, EventTypeEnum } from "@/models";
import { getProperty } from "@/composables/use-get-property";
import { DataLayerInteractionsEnum } from "@/enums/dataLayerInteractions.enum";
import JourneyLayout from "@/components/layout/JourneyLayout/JourneyLayout.vue";
import Question from "@/components/general/Question/Question.vue";
import Dropdown from "@/components/general/Dropdown/Dropdown.vue";
import TextField from "@/components/general/TextField/TextField.vue";
import Button from "@/components/general/Button/Button.vue";
import PageHeading from "@/components/general/PageHeading/PageHeading.vue";
import LocateBank from "@/components/general/LocateBank/LocateBank.vue";
import DirectDebit from "./DirectDebit.vue";
import TrustpilotCard from "./TrustpilotCard.vue";

// Stores
const global = useGlobalStore();
const dataLayer = useDataLayerStore();
const banking = useBankingStore();

const emit = defineEmits<{
    submitSection: [currentPage: PageEnum, nextPage: PageEnum];
}>();

const v$ = useVuelidate(bankDetailsRules(), banking);

const triggerBankLookup = async (): Promise<void> => {
    dataLayer.triggerEvent(EventTypeEnum.PageInteraction, "btn|bank lookup");
    const sortCodeValid = await v$.value.sortCode.$validate();
    const accountNumberValid = await v$.value.accountNumber.$validate();
    dataLayer.errors = v$.value.$errors;

    if (v$.value.sortCode.$error) {
        dataLayer.triggerAdobeEvent(
            AdobeEventTypeEnum.FormFieldError,
            "sortCode"
        );
    }

    if (v$.value.accountNumber.$error) {
        dataLayer.triggerAdobeEvent(
            AdobeEventTypeEnum.FormFieldError,
            "accountNumber"
        );
    }

    if (sortCodeValid && accountNumberValid) {
        banking.validBank = await banking.bankLookup();
        if (!banking.validBank) {
            banking.showManualAddress(true, false);
        } else {
            banking.showAddress = true;
            banking.validBank = true;
        }
    }
};

const getErrorMessage = (path: string | undefined): string => {
    if (!path) return "";

    const node = getProperty(path, v$.value);
    const errors = node?.$errors as ErrorObject[] | undefined;

    const message = errors?.[0]?.$message;
    return typeof message === "string" ? message : "";
};

const dataLayerEvents = (trackingEvent: DataLayerInteractionsEnum) => {
    switch (trackingEvent) {
        case DataLayerInteractionsEnum.AccountNumber:
        case DataLayerInteractionsEnum.SortCode:
        case DataLayerInteractionsEnum.BankName:
        case DataLayerInteractionsEnum.Branch:
        case DataLayerInteractionsEnum.BankAddress1:
        case DataLayerInteractionsEnum.BankAddress2:
        case DataLayerInteractionsEnum.BankAddress3:
        case DataLayerInteractionsEnum.AddressPostcode:
            dataLayer.triggerEvent(EventTypeEnum.QuestComplete, trackingEvent);
            break;
        case DataLayerInteractionsEnum.QuestionPremium:
        case DataLayerInteractionsEnum.BtnSaveBankAddress:
        case DataLayerInteractionsEnum.ButtonCancel:
            dataLayer.triggerEvent(
                EventTypeEnum.PageInteraction,
                trackingEvent
            );
            break;
    }
};

const submitSection = async (
    currentPage: PageEnum,
    nextPage: PageEnum
): Promise<void> => {
    dataLayer.triggerEvent(
        EventTypeEnum.PageInteraction,
        "button|View Summary"
    );
    dataLayer.errors = [];
    const isFormCorrect = await v$.value.$validate();
    if (!isFormCorrect) {
        dataLayer.errors = v$.value.$errors;
        // TODO: Remove below line when removing the old dataLayer
        dataLayer.triggerEvent(EventTypeEnum.ErrorLoad);

        for (const error of v$.value.$errors) {
            dataLayer.triggerAdobeEvent(
                AdobeEventTypeEnum.FormFieldError,
                error.$propertyPath
            );
        }
        banking.showManualAddress(true, false);
        document
            .querySelector(".form-group .error")
            ?.closest(".form-group")
            ?.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
    }
    emit("submitSection", currentPage, nextPage);
};

const detailChanged = (key: string) => {
    const target =
        key === "bankPostcode" ? v$.value.address.postcode : v$.value[key];
    target.$touch();
    if (target.$error) {
        dataLayer.errors = v$.value.$errors;
        dataLayer.triggerEvent(EventTypeEnum.ErrorLoad, key);
    }
    dataLayer.triggerEvent(EventTypeEnum.QuestComplete, key);
};

const useThisAddress = async (): Promise<void> => {
    dataLayer.triggerEvent(
        EventTypeEnum.PageInteraction,
        "btn|save bank address"
    );
    const bankNameValid = await v$.value.bankName.$validate();
    const branchValid = await v$.value.branch.$validate();
    const postcodeValid = await v$.value.address.postcode.$validate();
    dataLayer.errors = v$.value.$errors;
    if (v$.value.bankName.$error) {
        dataLayer.triggerAdobeEvent(
            AdobeEventTypeEnum.FormFieldError,
            "bankName"
        );
    }
    if (v$.value.branch.$error) {
        dataLayer.triggerAdobeEvent(
            AdobeEventTypeEnum.FormFieldError,
            "branch"
        );
    }
    if (v$.value.address.postcode.$error) {
        dataLayer.triggerAdobeEvent(
            AdobeEventTypeEnum.FormFieldError,
            "address.postcode"
        );
    }

    if (bankNameValid && branchValid && postcodeValid) {
        banking.showAddress = true;
        banking.validBank = true;
        banking.showManualAddress(false, false);
        dataLayer.triggerEvent(
            EventTypeEnum.PageInteraction,
            "button|use this address"
        );
    }
};

defineExpose({
    v$,
    triggerBankLookup,
    submitSection,
    detailChanged,
    useThisAddress,
    dataLayerEvents,
    getErrorMessage,
});
</script>

<template>
    <JourneyLayout>
        <template #main>
            <div :id="PageEnum.PaymentDetails">
                <PageHeading>Payment Details</PageHeading>
                <p>
                    Please pay L&G Direct Debits from the account detailed in
                    this instruction subject to the safeguards assured by the
                    Direct Debit Guarantee. I understand that this instruction
                    may remain with Legal & General and, if so, details will be
                    passed electronically to my Bank / Building Society.
                </p>
                <p>
                    It is required that you are the account holder and are
                    therefore able to authorise debits on this account. If you
                    are not the account holder you cannot complete this online
                    application.
                </p>
                <form name="form5" method="post" data-vv-scope="form5">
                    <div class="header-layout">
                        <h3 class="section-header">
                            Your bank account details
                        </h3>
                        <span class="secure-payment">
                            <img
                                class="secure-padlock"
                                src="@img/secure-padlock-icon.svg"
                                alt="Secure"
                            />
                            100% Secure
                        </span>
                    </div>

                    <div id="bankDetails">
                        <LocateBank
                            :has-error="{
                                accountNumber: v$.accountNumber.$error,
                                sortCode: v$.sortCode.$error,
                                bankName: v$.sortCode.$error,
                                branch: v$.branch.$error,
                                postcode: v$.address.postcode.$error,
                            }"
                            :get-error-message="getErrorMessage"
                            @change-detail="detailChanged"
                            @enter-manual="banking.manualAddress = true"
                            @trigger-lookup="triggerBankLookup"
                            @trigger-tracking="dataLayerEvents"
                            @use-address="useThisAddress"
                        />
                    </div>
                    <div class="gap">
                        <h3 class="section-header"> Your Bank Details </h3>
                        <div
                            v-if="banking.showAddress"
                            class="address-box check gap-bottom"
                        >
                            <p>{{ banking.bankName }}</p>
                            <p>{{ banking.branch }}</p>
                            <p>{{ banking.address.line1 }}</p>
                            <p>{{ banking.address.line2 }}</p>
                            <p>{{ banking.address.line3 }}</p>
                            <p>{{ banking.address.postcode }}</p>
                        </div>
                        <Question
                            id="account-holder"
                            label="Account holders name:"
                            :error-message="getErrorMessage('accountHolder')"
                        >
                            <TextField
                                id="account-holder-field"
                                v-model="banking.accountHolder"
                                type="text"
                                name="account-holder"
                                placeholder="Required"
                                :has-error="v$.accountHolder.$error"
                                @blur="detailChanged('accountHolder')"
                            />
                        </Question>
                        <Question
                            id="direct-debit-day"
                            label="Direct debit date:"
                            :error-message="getErrorMessage('directDebitDay')"
                        >
                            <Dropdown
                                id="direct-debit-day-dropdown"
                                v-model="banking.directDebitDay"
                                name="direct-debit-day"
                                aria-label="Direct debit date"
                                :options="
                                    [...Array(28)].map((_, i) => ({
                                        value: global.leftPad(i + 1),
                                        label: global.leftPad(i + 1),
                                    }))
                                "
                                placeholder="Day"
                                placeholder-value="0"
                                :has-error="v$.directDebitDay.$error"
                                @blur="v$.directDebitDay.$touch"
                                @change="
                                    dataLayer.triggerEvent(
                                        EventTypeEnum.QuestComplete,
                                        'directDebitDate'
                                    )
                                "
                            />
                        </Question>
                    </div>
                    <DirectDebit />
                </form>
            </div>
            <div class="trustpilot-img-inline">
                <TrustpilotCard />
            </div>
            <div>
                <Button
                    id="submitPaymentDetails"
                    btn-type="primary"
                    tabindex="0"
                    @click.prevent="
                        submitSection(
                            PageEnum.PaymentDetails,
                            PageEnum.YourPlan
                        )
                    "
                >
                    View Summary
                </Button>
            </div>
        </template>
        <template #aside>
            <div class="trustpilot-img-container">
                <TrustpilotCard />
            </div>
        </template>
    </JourneyLayout>
</template>

<style lang="css" scoped>
.header-layout {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 1.5rem;
    flex-wrap: nowrap;
    min-width: 100%;
    margin-top: 1.5rem;
}

.section-header {
    font-size: 20px;
    font-weight: 400;
    width: 20rem;
    white-space: nowrap;
    flex-shrink: 0;
}

.secure-payment {
    display: flex;
    align-items: center;
    white-space: nowrap;
    flex-shrink: 0;
}

.secure-padlock {
    margin-right: 0.5rem;
}

.section-card {
    background: var(--color-background-alt);
    padding: 1rem;
}

.gap {
    margin: 1.5rem 0;
}

.gap-top {
    margin-top: 2rem;
}

.gap-bottom {
    margin-bottom: 2rem;
}

.button-layout {
    display: flex;
    gap: 1rem;
    justify-content: space-between;

    &.column {
        flex-direction: column;
    }
}

.direct-debit-logo {
    margin-bottom: 1rem;
}

.address-box {
    display: flex;
    flex-direction: column;
    background: var(--selection-background);
    border: 2px solid var(--light-blue);
    margin-top: 5px;
    border-radius: 4px;
    padding: 1.5rem;

    &.check {
        background-image: url("@/assets/img/tick.png");
        background-repeat: no-repeat;
        background-size: 30px 30px;
        background-position: 97.5% 15px;
    }

    p {
        margin: 0;
    }
}

.trustpilot-img-container {
    position: relative;

    .trustpilot-card {
        position: absolute;
        top: 19.75rem;
        margin-left: 1rem;
    }
}

.trustpilot-img-inline {
    .trustpilot-card {
        margin-bottom: 2rem;
    }

    @media screen and (width <= 584px) {
        .trustpilot-card {
            margin: 1rem auto;
        }
    }
}

.trustpilot-img-container,
.trustpilot-img-inline {
    .trustpilot-card {
        display: flex;
        flex-direction: column;
        gap: 2rem;
        border-radius: 5px;
        background: var(--color-primary);
        max-width: 285px;
        min-width: 200px;
        padding: 1rem;

        .trustpilot-logo {
            width: 100%;
        }

        .trustpilot-text {
            color: var(--off-white);
            text-align: center;
            font-size: 1.25rem;
            font-weight: 800;
        }
    }
}

@media screen and (width <= 992px) {
    .trustpilot-img-container {
        display: none;
    }
}

@media screen and (width >= 993px) {
    .trustpilot-img-inline {
        display: none;
    }
}

@media screen and (width <= 768px) {
    .header-layout {
        flex-wrap: wrap;
        gap: 0;
    }

    .section-header {
        width: 100%;
        white-space: normal;
        text-overflow: unset;
    }

    .secure-payment {
        margin-top: 0.5rem;
    }

    .button-layout {
        flex-direction: column;
    }
}
</style>
